/* Sticker Select — product configurator, artwork upload, proof choice.
 * Pricing: this file NEVER calculates a charge. It reads Shopify variant prices (rendered by Liquid)
 * and sends the variant ID to the cart. Checkout charges the variant price.
 */
(() => {
  'use strict';
  const SS = (window.SS = window.SS || {});

  /* ---------- helpers ---------- */
  SS.money = SS.money || function (cents, format) {
    const fmt = format || SS.moneyFormat || '${{amount}}';
    const value = Number(cents || 0) / 100;
    const withDelims = (n, d, t = ',', s = '.') => {
      const parts = n.toFixed(d).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
      return parts.join(s);
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0);
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        default: return withDelims(value, 2);
      }
    });
  };
  /** Per-unit price for display only (may need sub-cent precision). */
  SS.unitPrice = function (cents, units) {
    if (!units) return '';
    const each = cents / units; // cents
    if (each >= 100) return SS.money(Math.round(each));
    // under $1: show 2 decimals, or 3 when a fraction of a cent matters (e.g. $0.138)
    let s = (each / 100).toFixed(3);
    if (s.endsWith('0')) s = s.slice(0, -1);
    const sym = (SS.money(0).match(/^[^\d]*/) || ['$'])[0];
    return sym + s;
  };
  SS.track = function (name, data) {
    try { window.Shopify && Shopify.analytics && Shopify.analytics.publish && Shopify.analytics.publish(name, data || {}); } catch (e) { /* noop */ }
    document.dispatchEvent(new CustomEvent('ss:track', { detail: { name, data } }));
  };
  const parseDims = (str) => {
    const nums = String(str).replace(/(\d),(\d)/g, '$1$2').match(/\d+(?:\.\d+)?/g);
    if (!nums || !nums.length) return null;
    const w = parseFloat(nums[0]);
    const h = nums[1] ? parseFloat(nums[1]) : w;
    return { w, h };
  };
  const parseQty = (str) => {
    const n = parseInt(String(str).replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };
  const fmtNum = (n) => Number(n).toLocaleString();
  const fmtIn = (n) => `${Number(n).toString()}"`;
  const fileExt = (name) => (String(name).split('.').pop() || '').toLowerCase();
  const fmtBytes = (b) => (b > 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB');
  SS.parseDims = parseDims; SS.parseQty = parseQty;

  /* ---------- <artwork-upload> ---------- */
  const PREVIEWABLE = ['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'];
  const RASTER = ['png', 'jpg', 'jpeg', 'webp', 'gif'];

  class ArtworkUpload extends HTMLElement {
    connectedCallback() {
      if (this._init) return; this._init = true;
      this.accepted = (this.dataset.accepted || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.maxBytes = (parseFloat(this.dataset.maxMb) || 50) * 1048576;
      this.state = { status: 'empty' };
      this.input = this.querySelector('[data-file-input]');
      this.drop = this.querySelector('[data-drop]');
      this.card = this.querySelector('[data-file-card]');
      this.errorEl = this.querySelector('[data-upload-error]');
      this.warnEl = this.querySelector('[data-quality-warning]');
      this.input.addEventListener('change', () => this.input.files[0] && this.handleFile(this.input.files[0]));
      this.querySelector('[data-remove]').addEventListener('click', () => this.clear(true));
      ['dragenter', 'dragover'].forEach((ev) => this.drop.addEventListener(ev, (e) => { e.preventDefault(); this.drop.classList.add('is-drag'); }));
      ['dragleave', 'drop'].forEach((ev) => this.drop.addEventListener(ev, (e) => { e.preventDefault(); this.drop.classList.remove('is-drag'); }));
      this.drop.addEventListener('drop', (e) => { const f = e.dataTransfer && e.dataTransfer.files[0]; if (f) this.handleFile(f); });
      if (this.dataset.fieldPrefix) {
        this.hiddenRef = this.appendHidden(`${this.dataset.fieldPrefix}[Artwork ID]`);
        this.hiddenName = this.appendHidden(`${this.dataset.fieldPrefix}[Artwork file]`);
      }
    }
    appendHidden(name) { const i = document.createElement('input'); i.type = 'hidden'; i.name = name; this.appendChild(i); return i; }
    get value() { return this.state.status === 'ready' ? { ref: this.state.ref, filename: this.state.filename, width: this.state.width, height: this.state.height } : null; }
    get busy() { return this.state.status === 'uploading'; }

    validate() {
      if (this.state.status === 'uploading') return 'Please wait for your artwork to finish uploading.';
      if (this.state.status === 'error') return this.state.error || 'Your artwork did not upload. Please try again.';
      if (this.hasAttribute('data-required') && this.state.status !== 'ready') return 'Please upload your artwork.';
      return null;
    }
    showError(msg) {
      this.errorEl.textContent = msg || '';
      this.errorEl.hidden = !msg;
      this.input.setAttribute('aria-invalid', msg ? 'true' : 'false');
    }
    clientCheck(file) {
      const ext = fileExt(file.name);
      if (!file.size) return 'That file looks empty. Please choose another.';
      if (this.accepted.length && !this.accepted.includes(ext)) return `“.${ext}” files aren't supported. Please upload ${this.accepted.map((a) => a.toUpperCase()).join(', ')}.`;
      if (file.size > this.maxBytes) return `That file is ${fmtBytes(file.size)}. The maximum is ${Math.round(this.maxBytes / 1048576)} MB — try exporting a smaller PDF or PNG, or request a quote and we'll help.`;
      return null;
    }
    async handleFile(file) {
      this.showError('');
      const problem = this.clientCheck(file);
      if (problem) { this.input.value = ''; this.showError(problem); this.emit(); return; }
      if (this.state.previewUrl) URL.revokeObjectURL(this.state.previewUrl);
      const ext = fileExt(file.name);
      this.state = { status: 'uploading', filename: file.name, size: file.size, ext, previewUrl: PREVIEWABLE.includes(ext) ? URL.createObjectURL(file) : null };
      if (RASTER.includes(ext)) {
        try { const dims = await this.readDims(this.state.previewUrl); Object.assign(this.state, dims); } catch (e) { /* ignore */ }
      }
      this.render();
      this.emit();
      try {
        const res = await this.upload(file);
        if (!res || !res.ref) throw new Error('bad_response');
        Object.assign(this.state, { status: 'ready', ref: res.ref, filename: res.filename || file.name });
        SS.track('sticker_artwork_uploaded', { file_type: ext, file_size: file.size });
      } catch (err) {
        const msg = err && err.userMessage ? err.userMessage : 'Upload failed. Please check your connection and try again.';
        Object.assign(this.state, { status: 'error', error: msg });
        this.showError(msg);
      }
      this.input.value = '';
      this.render();
      this.emit();
    }
    readDims(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = reject; img.src = url;
      });
    }
    async upload(file) {
      const base = SS.backend || '/apps/sticker-select';
      let ticket;
      try {
        const r = await fetch(`${base}/upload-ticket`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!r.ok) throw new Error('ticket');
        ticket = await r.json();
      } catch (e) {
        const err = new Error('ticket'); err.userMessage = 'Our upload service is unavailable right now. Please try again in a minute, or contact us.'; throw err;
      }
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const fd = new FormData();
        fd.append('ticket', ticket.ticket);
        fd.append('file', file, file.name);
        xhr.open('POST', ticket.uploadUrl);
        xhr.responseType = 'json';
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) this.setProgress(e.loaded / e.total); };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) return resolve(xhr.response);
          const err = new Error('upload'); err.userMessage = (xhr.response && xhr.response.error) || 'Upload failed. Please try again.'; reject(err);
        };
        xhr.onerror = () => { const err = new Error('network'); err.userMessage = 'Upload failed — connection lost. Please try again.'; reject(err); };
        xhr.send(fd);
      });
    }
    setProgress(p) {
      const bar = this.querySelector('[data-progress-bar]');
      if (bar) bar.style.width = Math.round(p * 100) + '%';
      const st = this.querySelector('[data-file-status]');
      if (st && this.state.status === 'uploading') st.textContent = `Uploading… ${Math.round(p * 100)}%`;
    }
    /** Prefill from a previous order / cart line (artwork already stored privately). */
    setPreset({ ref, filename }) {
      if (!ref) return;
      this.state = { status: 'ready', ref, filename: filename || ref, preset: true };
      this.render(); this.emit();
    }
    clear(focus) {
      if (this.state.previewUrl) URL.revokeObjectURL(this.state.previewUrl);
      this.state = { status: 'empty' };
      this.showError(''); this.render(); this.emit();
      if (focus) this.input.focus();
    }
    render() {
      const s = this.state;
      const has = s.status !== 'empty';
      this.drop.hidden = has;
      this.card.hidden = !has;
      this.classList.toggle('is-ready', s.status === 'ready');
      this.classList.toggle('is-error', s.status === 'error');
      this.querySelector('[data-file-name]').textContent = s.filename || '';
      const status = this.querySelector('[data-file-status]');
      status.innerHTML = '';
      if (s.status === 'uploading') status.textContent = 'Uploading…';
      if (s.status === 'ready') status.innerHTML = `<span class="ok">✓ ${s.preset ? 'Using artwork from your previous order' : 'Uploaded securely'}</span>${s.size ? ' · ' + fmtBytes(s.size) : ''}${s.width ? ` · ${s.width}×${s.height}px` : ''}`;
      if (s.status === 'error') status.textContent = 'Upload failed';
      this.querySelector('[data-progress]').hidden = s.status !== 'uploading';
      if (s.status === 'uploading') this.setProgress(0.02);
      const thumb = this.querySelector('[data-thumb]');
      thumb.hidden = !s.previewUrl; this.querySelector('[data-thumb-icon]').hidden = !!s.previewUrl;
      if (s.previewUrl) thumb.src = s.previewUrl; else thumb.removeAttribute('src');
      if (this.hiddenRef) { this.hiddenRef.value = s.status === 'ready' ? s.ref : ''; this.hiddenName.value = s.status === 'ready' ? s.filename : ''; }
    }
    /** Print-quality hint for raster files: effective DPI at the chosen size. */
    checkQuality(dims) {
      const s = this.state;
      if (!this.warnEl) return;
      if (!dims || !s.width || !RASTER.includes(s.ext || '')) { this.warnEl.hidden = true; return; }
      const dpi = Math.min(s.width / dims.w, s.height / dims.h);
      if (dpi < 150) {
        this.warnEl.innerHTML = `<strong>Heads up:</strong> at ${fmtIn(dims.w)} × ${fmtIn(dims.h)} this image is about ${Math.round(dpi)} DPI and may print blurry. A higher-resolution file (300 DPI) or a vector file will look sharper — or choose a proof and we'll check it.`;
        this.warnEl.hidden = false;
      } else this.warnEl.hidden = true;
    }
    emit() { this.dispatchEvent(new CustomEvent('artwork:change', { bubbles: true, detail: this.state })); }
  }
  if (!customElements.get('artwork-upload')) customElements.define('artwork-upload', ArtworkUpload);

  /* ---------- <product-configurator> ---------- */
  class ProductConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._init) return; this._init = true;
      try { this.product = JSON.parse(this.querySelector('[data-product-json]').textContent); }
      catch (e) { this.product = null; }
      this.form = this.querySelector('[data-config-form]');
      this.sizeIndex = parseInt(this.dataset.sizeIndex, 10);
      this.qtyIndex = parseInt(this.dataset.qtyIndex, 10);
      this.unitLabel = this.dataset.unitLabel || 'items';
      this.requireArtwork = this.dataset.requireArtwork === 'true';
      this.proofEnabled = this.dataset.proofEnabled === 'true';
      this.customMin = parseFloat(this.dataset.customMin) || 1;
      this.customMax = parseFloat(this.dataset.customMax) || 12;
      this.upload = this.querySelector('artwork-upload');
      this.errorsBox = this.querySelector('[data-form-errors]');
      this.submitBtn = this.querySelector('[data-submit]');
      this.state = { options: [], custom: false, proof: null, ack: false };

      if (!this.product || !this.product.variants || !this.product.variants.length) {
        this.fatal('Pricing for this product isn’t set up yet. Please contact us to order.');
        return;
      }
      // initial selection from checked radios / first variant
      const initial = this.product.variants.find((v) => String(v.id) === this.querySelector('[data-variant-input]').value) || this.product.variants[0];
      this.state.options = initial.options.slice();
      this.syncRadios();
      this.drawShapes();

      this.form.addEventListener('change', (e) => this.onChange(e));
      this.form.addEventListener('input', (e) => { if (e.target.matches('[data-custom-w],[data-custom-h]')) this.onCustomInput(); });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      this.addEventListener('artwork:change', () => { this.updateQuality(); this.updateStage(); this.clearErrors(); });
      const sticky = this.querySelector('[data-sticky-submit]');
      if (sticky) sticky.addEventListener('click', () => this.submit());
      this.setupSticky();
      this.setupThumbs();
      this.initChoices();
      this.prefillFromUrl();
      this.update();
      SS.track('sticker_product_configurator_viewed', { product_id: this.product.id });
    }

    fatal(msg) {
      this.errorsBox.innerHTML = `<p class="mb-0">${msg}</p>`; this.errorsBox.hidden = false;
      this.submitBtn.disabled = true;
    }

    /* --- option values & variant resolution --- */
    optionValues(i) { return (this.product.options && this.product.variants) ? [...new Set(this.product.variants.map((v) => v.options[i]))] : []; }
    findVariant(opts) { return this.product.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null; }

    /** Custom size → the smallest standard size (by area) that contains W×H (either orientation). */
    bucketForCustom(w, h) {
      if (this.sizeIndex < 0) return null;
      const cands = this.optionValues(this.sizeIndex)
        .map((val) => ({ val, d: parseDims(val) }))
        .filter((c) => c.d && ((c.d.w >= w && c.d.h >= h) || (c.d.w >= h && c.d.h >= w)))
        .filter((c) => { const o = this.state.options.slice(); o[this.sizeIndex] = c.val; const v = this.findVariant(o); return v && v.available; })
        .sort((a, b) => a.d.w * a.d.h - b.d.w * b.d.h);
      return cands[0] ? cands[0].val : null;
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-choice-input]')) {
        this.applyChoiceRules();
        this.clearErrors();
        const g = t.closest('[data-choice-group]');
        SS.track('sticker_option_selected', { option: g ? g.dataset.choiceName : '', value: t.value });
        return;
      }
      if (t.matches('[data-option-index]')) {
        const i = parseInt(t.dataset.optionIndex, 10);
        if (t.value === '__custom') {
          this.state.custom = true;
          this.querySelector('[data-custom-size]').hidden = false;
          this.querySelector('[data-custom-w]').focus();
          this.onCustomInput();
        } else {
          if (i === this.sizeIndex) { this.state.custom = false; const cs = this.querySelector('[data-custom-size]'); if (cs) cs.hidden = true; }
          this.state.options[i] = t.value;
          if (i === this.sizeIndex) SS.track('sticker_size_selected', { size: t.value });
          else if (i === this.qtyIndex) SS.track('sticker_quantity_selected', { quantity: parseQty(t.value) });
          else SS.track('sticker_option_selected', { option: this.product.options[i], value: t.value });
        }
        this.clearErrors();
        this.update();
      } else if (t.matches('[data-proof-input]')) {
        this.state.proof = t.value;
        const ack = this.querySelector('[data-proof-ack]');
        if (ack) ack.hidden = t.value !== 'skipped';
        if (t.value !== 'skipped') { this.state.ack = false; const box = this.querySelector('[data-proof-ack-input]'); if (box) box.checked = false; }
        SS.track('sticker_proof_selected', { proof: t.value });
        this.clearErrors(); this.update();
      } else if (t.matches('[data-proof-ack-input]')) {
        this.state.ack = t.checked; this.clearErrors(); this.update();
      }
    }

    customDims() {
      const w = parseFloat(this.querySelector('[data-custom-w]')?.value);
      const h = parseFloat(this.querySelector('[data-custom-h]')?.value);
      return { w, h };
    }
    customError() {
      const { w, h } = this.customDims();
      if (!Number.isFinite(w) || !Number.isFinite(h)) return 'Enter a width and height for your custom size.';
      if (w < this.customMin || h < this.customMin) return `Custom sizes must be at least ${fmtIn(this.customMin)} on each side.`;
      if (w > this.customMax || h > this.customMax) return `Custom sizes can be up to ${fmtIn(this.customMax)} on each side. For bigger sizes, please request a quote.`;
      if (!this.bucketForCustom(w, h)) return 'We can’t price that size online yet. Please request a quote.';
      return null;
    }
    onCustomInput() {
      const errEl = this.querySelector('[data-custom-error]');
      const pricedEl = this.querySelector('[data-custom-priced]');
      const { w, h } = this.customDims();
      const touched = Number.isFinite(w) && Number.isFinite(h);
      const err = this.customError();
      if (errEl) { errEl.textContent = touched && err ? err : ''; errEl.hidden = !(touched && err); }
      if (!err) {
        const bucket = this.bucketForCustom(w, h);
        this.state.options[this.sizeIndex] = bucket;
        if (pricedEl) { pricedEl.textContent = `${fmtIn(w)} × ${fmtIn(h)} — priced as ${bucket}`; pricedEl.hidden = false; }
        SS.track('sticker_size_selected', { size: 'custom', width: w, height: h, priced_as: bucket });
      } else if (pricedEl) pricedEl.hidden = true;
      this.update();
    }

    currentVariant() {
      if (this.state.custom && this.customError()) return null;
      return this.findVariant(this.state.options);
    }
    packQty() { return this.qtyIndex >= 0 ? parseQty(this.state.options[this.qtyIndex]) : 1; }
    sizeDims() {
      if (this.state.custom) { const d = this.customDims(); return Number.isFinite(d.w) && Number.isFinite(d.h) ? d : null; }
      return this.sizeIndex >= 0 ? parseDims(this.state.options[this.sizeIndex]) : null;
    }

    /* --- rendering --- */
    syncRadios() {
      this.querySelectorAll('[data-option-index]').forEach((r) => {
        const i = parseInt(r.dataset.optionIndex, 10);
        if (r.value === '__custom') r.checked = this.state.custom;
        else r.checked = !(i === this.sizeIndex && this.state.custom) && this.state.options[i] === r.value;
      });
    }
    drawShapes() {
      const shapes = [...this.querySelectorAll('[data-shape]')];
      const dims = shapes.map((s) => parseDims(s.dataset.shape)).filter(Boolean);
      const max = Math.max(1, ...dims.map((d) => Math.max(d.w, d.h)));
      shapes.forEach((s) => {
        const d = parseDims(s.dataset.shape); if (!d) return;
        s.style.width = Math.max(14, (d.w / max) * 44) + 'px';
        s.style.height = Math.max(14, (d.h / max) * 44) + 'px';
      });
    }
    update() {
      const v = this.currentVariant();
      const qty = this.packQty();
      this.querySelector('[data-variant-input]').value = v ? v.id : '';
      // availability of option values given other selections
      this.querySelectorAll('[data-option-index]').forEach((r) => {
        if (r.value === '__custom') return;
        const i = parseInt(r.dataset.optionIndex, 10);
        const o = this.state.options.slice(); o[i] = r.value;
        const cand = this.findVariant(o);
        r.closest('.opt').classList.toggle('is-unavailable', !cand || !cand.available);
        r.disabled = !cand || !cand.available;
      });
      // per-quantity unit price + savings
      if (this.qtyIndex >= 0) {
        const vals = this.optionValues(this.qtyIndex);
        let baseEach = null;
        const eachFor = (val) => { const o = this.state.options.slice(); o[this.qtyIndex] = val; const cv = this.findVariant(o); return cv ? cv.price / parseQty(val) : null; };
        vals.forEach((val) => { const e = eachFor(val); if (e != null && baseEach == null) baseEach = e; });
        vals.forEach((val) => {
          const e = eachFor(val);
          const eachEl = this.querySelector(`[data-qty-each="${CSS.escape(val)}"]`);
          const saveEl = this.querySelector(`[data-qty-save="${CSS.escape(val)}"]`);
          if (eachEl) eachEl.textContent = e != null ? `${SS.unitPrice(e * parseQty(val), parseQty(val))} ea` : 'n/a';
          if (saveEl) {
            const pct = baseEach && e != null ? Math.round((1 - e / baseEach) * 100) : 0;
            saveEl.textContent = pct >= 5 ? `Save ${pct}%` : '';
          }
        });
      }
      // summary
      const totalEl = this.querySelector('[data-sum-total]');
      const qtyEl = this.querySelector('[data-sum-qty]');
      const eachEl = this.querySelector('[data-sum-each]');
      const saveEl = this.querySelector('[data-sum-save]');
      const cmpEl = this.querySelector('[data-sum-compare]');
      const stickyTotal = this.querySelector('[data-sticky-total]');
      const stickyMeta = this.querySelector('[data-sticky-meta]');
      if (v && v.available) {
        totalEl.textContent = SS.money(v.price);
        qtyEl.textContent = `${fmtNum(qty)} ${this.unitLabel}`;
        eachEl.textContent = qty > 1 ? `≈ ${SS.unitPrice(v.price, qty)} each` : '';
        if (cmpEl) { cmpEl.hidden = !(v.compare > v.price); cmpEl.textContent = v.compare > v.price ? SS.money(v.compare) : ''; }
        if (stickyTotal) stickyTotal.textContent = SS.money(v.price);
        if (stickyMeta) stickyMeta.textContent = `${fmtNum(qty)} ${this.unitLabel}${qty > 1 ? ' · ' + SS.unitPrice(v.price, qty) + ' ea' : ''}`;
        const saveText = this.qtyIndex >= 0 ? this.querySelector(`[data-qty-save="${CSS.escape(this.state.options[this.qtyIndex])}"]`)?.textContent : '';
        saveEl.hidden = !saveText; saveEl.textContent = saveText ? `${saveText} vs. smallest quantity` : '';
      } else {
        totalEl.textContent = '—';
        qtyEl.textContent = this.state.custom ? 'Enter your custom size' : 'Choose your options';
        eachEl.textContent = v && !v.available ? 'This combination is currently unavailable.' : '';
        saveEl.hidden = true;
        if (stickyTotal) stickyTotal.textContent = '—';
        if (stickyMeta) stickyMeta.textContent = '';
      }
      // CTA label depends on proof choice
      const label = this.state.proof === 'required' ? 'Add to cart — pay after proof' : 'Add to cart';
      this.querySelector('[data-submit-label]').textContent = label;
      const st = this.querySelector('[data-sticky-submit]'); if (st) st.textContent = this.state.proof === 'required' ? 'Add — pay later' : 'Add to cart';
      this.syncRadios();
      this.updateQuality();
      if (v && !this.editKey) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    updateQuality() { if (this.upload) this.upload.checkQuality(this.sizeDims()); }
    updateStage() {
      const fig = document.querySelector('[data-stage-preview]');
      if (!fig || !this.upload) return;
      const s = this.upload.state;
      const img = fig.querySelector('[data-stage-img]');
      if (s.previewUrl) { img.src = s.previewUrl; fig.querySelector('[data-stage-caption]').textContent = s.filename; fig.hidden = false; }
      else { fig.hidden = true; img.removeAttribute('src'); }
    }

    /* --- validation --- */
    collectErrors() {
      const errs = [];
      if (this.requireArtwork && this.upload) { const e = this.upload.validate(); if (e) errs.push({ msg: e, el: this.upload.querySelector('[data-file-input]') }); }
      if (this.state.custom) { const e = this.customError(); if (e) errs.push({ msg: e, el: this.querySelector('[data-custom-w]') }); }
      if (this.sizeIndex >= 0 && !this.state.options[this.sizeIndex]) errs.push({ msg: 'Please choose a size.', el: this.querySelector('[data-step="size"] input') });
      if (this.qtyIndex >= 0 && !this.state.options[this.qtyIndex]) errs.push({ msg: 'Please choose a quantity.', el: this.querySelector('[data-step="quantity"] input') });
      const v = this.currentVariant();
      if (!this.state.custom || !this.customError()) {
        if (!v) errs.push({ msg: 'That combination isn’t available. Please choose a different size or quantity.', el: this.querySelector('[data-step="quantity"] input') });
        else if (!v.available) errs.push({ msg: 'That option is currently unavailable.', el: null });
      }
      if (this.proofEnabled) {
        const pe = this.querySelector('[data-proof-error]');
        let msg = null;
        if (!this.state.proof) msg = 'Please choose whether you’d like a proof.';
        else if (this.state.proof === 'skipped' && !this.state.ack) msg = 'Please confirm your artwork is print-ready, or choose a proof.';
        if (pe) { pe.textContent = msg || ''; pe.hidden = !msg; }
        if (msg) errs.push({ msg, el: this.querySelector(this.state.proof === 'skipped' ? '[data-proof-ack-input]' : '[data-proof-input]') });
      }
      (this.choiceGroups || []).forEach((g) => { if (!g.el.hidden && !this.choiceValue(g.name)) errs.push({ msg: `Please choose a ${g.name.toLowerCase()}.`, el: g.el.querySelector('[data-choice-input]') }); });
      this.querySelectorAll('[data-property][required]').forEach((f) => { if (!f.value.trim()) errs.push({ msg: `Please fill in “${f.dataset.property}”.`, el: f }); });
      return errs;
    }
    clearErrors() { if (this.errorsBox) { this.errorsBox.hidden = true; this.errorsBox.innerHTML = ''; } }
    showErrors(errs) {
      this.errorsBox.innerHTML = `<p class="mb-0"><strong>Almost there:</strong></p><ul style="margin:.25rem 0 0;padding-left:1.1rem">${errs.map((e) => `<li>${e.msg}</li>`).join('')}</ul>`;
      this.errorsBox.hidden = false;
      const first = errs.find((e) => e.el);
      if (first) { first.el.closest('fieldset, .field, artwork-upload')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => first.el.focus({ preventScroll: true }), 350); }
      else this.errorsBox.focus();
    }

    /* --- choice buttons (finish, cut type, format, peel...): saved as line item properties, no price impact --- */
    initChoices() {
      const cond = (str) => {
        const i = String(str || '').indexOf('=');
        if (i < 0) return null;
        const name = str.slice(0, i).trim(), value = str.slice(i + 1).trim();
        return name && value ? { name, value } : null;
      };
      this.choiceGroups = [...this.querySelectorAll('[data-choice-group]')].map((el) => ({
        el,
        name: el.dataset.choiceName,
        showIf: cond(el.dataset.showIf),
        rules: String(el.dataset.rules || '').split(/\r?\n|;/).map((line) => {
          const i = line.indexOf('|');
          if (i < 0) return null;
          const value = line.slice(0, i).trim(), c = cond(line.slice(i + 1));
          return value && c ? { value, cond: c } : null;
        }).filter(Boolean)
      }));
      this.applyChoiceRules();
    }
    choiceValue(name) {
      const g = (this.choiceGroups || []).find((x) => x.name.toLowerCase() === String(name).toLowerCase());
      if (!g || g.el.hidden) return null;
      const r = g.el.querySelector('[data-choice-input]:checked');
      return r ? r.value : null;
    }
    applyChoiceRules() {
      if (!this.choiceGroups || !this.choiceGroups.length) return;
      const eq = (a, b) => a != null && String(a).toLowerCase() === String(b).toLowerCase();
      for (let pass = 0; pass < 3; pass++) { // a few passes so chained rules settle
        this.choiceGroups.forEach((g) => {
          g.el.hidden = !!g.showIf && !eq(this.choiceValue(g.showIf.name), g.showIf.value);
          const notes = [];
          const inputs = [...g.el.querySelectorAll('[data-choice-input]')];
          inputs.forEach((inp) => {
            const block = g.rules.find((r) => eq(r.value, inp.value) && eq(this.choiceValue(r.cond.name), r.cond.value));
            inp.disabled = !!block;
            if (block) notes.push(`${inp.value} isn’t available with ${block.cond.value}.`);
          });
          const cur = inputs.find((i) => i.checked);
          if (!cur || cur.disabled) { const first = inputs.find((i) => !i.disabled); inputs.forEach((i) => { i.checked = i === first; }); }
          const note = g.el.querySelector('[data-choice-note]');
          if (note) { note.textContent = notes.join(' '); note.hidden = !notes.length; }
        });
      }
      this.choiceGroups.forEach((g) => {
        const key = 'choice' + g.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').replace(/-([a-z0-9])/g, (m, c) => c.toUpperCase()).replace(/^./, (c) => c.toUpperCase());
        const v = this.choiceValue(g.name);
        if (v) this.dataset[key] = v.toLowerCase(); else delete this.dataset[key];
      });
      // keep step numbers sequential when groups appear/disappear
      let n = 0;
      this.querySelectorAll('.cstep').forEach((fs) => { const num = fs.querySelector('.cstep__num'); if (num && !fs.hidden) num.textContent = String(++n); });
    }

    buildProperties() {
      const p = {};
      const art = this.upload ? this.upload.value : null;
      if (art) { p['Artwork'] = art.filename; p['Artwork ID'] = art.ref; }
      if (this.state.custom) {
        const { w, h } = this.customDims();
        p['Custom size'] = `${fmtIn(w)} × ${fmtIn(h)} (priced as ${this.state.options[this.sizeIndex]})`;
        p['_custom_w'] = String(w); p['_custom_h'] = String(h);
      }
      if (this.proofEnabled) {
        p['Proof'] = this.state.proof === 'required' ? 'PROOF REQUIRED' : 'PROOF SKIPPED';
        p['_proof'] = this.state.proof;
        if (this.state.proof === 'skipped') p['_proof_ack'] = new Date().toISOString();
      }
      (this.choiceGroups || []).forEach((g) => { const v = this.choiceValue(g.name); if (v) p[g.name] = v; });
      this.querySelectorAll('[data-property]').forEach((f) => { const val = f.value.trim(); if (val) p[f.dataset.property] = val; });
      p['_pack_qty'] = String(this.packQty());
      p['_unit'] = this.unitLabel;
      return p;
    }

    async submit() {
      if (this.submitting) return;
      const errs = this.collectErrors();
      if (errs.length) { this.showErrors(errs); return; }
      const v = this.currentVariant();
      this.submitting = true; this.submitBtn.classList.add('is-loading'); this.submitBtn.disabled = true;
      try {
        if (this.editKey) {
          await fetch(`${SS.routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: this.editKey, quantity: 0 }) });
        }
        const res = await fetch(`${SS.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: v.id, quantity: 1, properties: this.buildProperties() }], sections: 'cart-drawer', sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          if (this.editItem) { // restore the line we removed so nothing is lost
            await fetch(`${SS.routes.cartAdd}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: this.editItem.variant_id, quantity: this.editItem.quantity, properties: this.editItem.properties }] }) });
          }
          throw new Error(data.description || data.message || 'Could not add to cart');
        }
        SS.track('sticker_added_to_cart', { variant_id: v.id, pack_qty: this.packQty(), proof: this.state.proof || 'n/a', custom_size: this.state.custom });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: data.sections, open: true } }));
        if (this.editKey) { this.editKey = null; window.location.href = SS.routes.cart; return; }
      } catch (err) {
        this.showErrors([{ msg: err.message || 'Something went wrong adding to cart. Please try again.', el: null }]);
      } finally {
        this.submitting = false; this.submitBtn.classList.remove('is-loading'); this.submitBtn.disabled = false;
      }
    }

    /* --- prefill: edit a cart line (?edit=key) or reorder (?reorder=1&…) --- */
    async prefillFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const apply = (props, variantId) => {
        const v = this.product.variants.find((x) => String(x.id) === String(variantId));
        if (v) this.state.options = v.options.slice();
        if (props['_custom_w'] && this.querySelector('[data-custom-w]')) {
          this.state.custom = true;
          this.querySelector('[data-custom-size]').hidden = false;
          this.querySelector('[data-custom-w]').value = props['_custom_w'];
          this.querySelector('[data-custom-h]').value = props['_custom_h'];
        }
        if (props['Artwork ID'] && this.upload) this.upload.setPreset({ ref: props['Artwork ID'], filename: props['Artwork'] });
        if (props['_proof'] && this.proofEnabled) {
          const r = this.querySelector(`[data-proof-input][value="${props['_proof']}"]`);
          if (r) { r.checked = true; this.state.proof = props['_proof']; this.querySelector('[data-proof-ack]').hidden = props['_proof'] !== 'skipped'; }
        }
        this.querySelectorAll('[data-property]').forEach((f) => { if (props[f.dataset.property]) f.value = props[f.dataset.property]; });
        for (let pass = 0; pass < 3; pass++) { // set in order, re-applying rules so dependent groups are visible
          (this.choiceGroups || []).forEach((g) => {
            const want = props[g.name];
            const inp = want && [...g.el.querySelectorAll('[data-choice-input]')].find((i) => i.value.toLowerCase() === String(want).toLowerCase());
            if (inp && !inp.disabled) inp.checked = true;
          });
          this.applyChoiceRules();
        }
        if (this.state.custom) this.onCustomInput(); else this.update();
      };
      if (params.get('edit')) {
        try {
          const cart = await (await fetch(`${SS.routes.cart}.js`, { headers: { Accept: 'application/json' } })).json();
          const item = cart.items.find((i) => i.key === params.get('edit'));
          if (item) {
            this.editKey = item.key; this.editItem = item;
            apply(item.properties || {}, item.variant_id);
            this.querySelector('[data-submit-label]').textContent = 'Update cart item';
          }
        } catch (e) { /* ignore */ }
      } else if (params.get('reorder')) {
        const props = {};
        if (params.get('artwork_ref')) { props['Artwork ID'] = params.get('artwork_ref'); props['Artwork'] = params.get('artwork_name') || ''; }
        if (params.get('w')) { props['_custom_w'] = params.get('w'); props['_custom_h'] = params.get('h'); }
        try { // only restore choice-button specs from the link (never artwork/proof/hidden fields)
          const specs = JSON.parse(params.get('specs') || '{}');
          (this.choiceGroups || []).forEach((g) => { if (typeof specs[g.name] === 'string') props[g.name] = specs[g.name]; });
        } catch (e) { /* ignore */ }
        apply(props, params.get('variant'));
        SS.track('sticker_reorder_started', { variant_id: params.get('variant') });
      }
    }

    setupSticky() {
      const bar = this.querySelector('[data-sticky-buy]');
      if (!bar || !('IntersectionObserver' in window)) return;
      let submitVisible = false, configVisible = false;
      const set = () => { const show = configVisible && !submitVisible; bar.classList.toggle('is-visible', show); bar.setAttribute('aria-hidden', String(!show)); bar.querySelector('button').tabIndex = show ? 0 : -1; };
      new IntersectionObserver(([e]) => { submitVisible = e.isIntersecting; set(); }).observe(this.submitBtn);
      new IntersectionObserver(([e]) => { configVisible = e.isIntersecting; set(); }, { rootMargin: '0px 0px -40% 0px' }).observe(this.form);
    }
    setupThumbs() {
      const main = document.querySelector('[data-product-image]');
      document.querySelectorAll('[data-thumb]').forEach((b) => b.addEventListener('click', () => {
        if (main && main.tagName === 'IMG') { main.removeAttribute('srcset'); main.src = b.dataset.thumb; }
      }));
    }
  }
  if (!customElements.get('product-configurator')) customElements.define('product-configurator', ProductConfigurator);
})();
